import {Component, ElementRef, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {AsyncPipe, formatDate} from "@angular/common";
import {ActivatedRoute} from "@angular/router";
import {Subscription, switchMap, take, timer} from "rxjs";
import {NgbModal, NgbModalRef, NgbNavModule, NgbPagination} from "@ng-bootstrap/ng-bootstrap";
import {
    Customer,
    MasterDataFileKind,
    MasterDataLoadRun,
    MasterDataLoadRunDetail,
    MasterDataLoadRunFile,
    ValidationFinding,
    ValidationReport,
} from "@app/_models/api";
import {ApiService} from "@app/_services/api.service";
import {GlobalService} from "@app/_services/global.service";
import {ToastService} from "@app/_services/toast.service";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {MasterDataLoadsService} from "@app/master-data/master-data-loads/master-data-loads.service";
import {FormsModule} from "@angular/forms";

interface FileKindOption {
    key: MasterDataFileKind;
    label: string;
    abbr: string;
}

// Help-modal guidance + sample-file content for one file kind. Columns and rows are
// tab-delimited on download to match the engine's file_specs.py header contract.
interface FileKindGuide {
    description: string;
    columns: string[];
    rows: string[][];
}

// One detail-modal tab: a file plus its parsed findings and its own pagination page.
interface FileDetailView {
    file: MasterDataLoadRunFile;
    findings: ValidationFinding[];
    page: number;
}

@Component({
    selector: 'app-master-data-loads',
    templateUrl: './master-data-loads.component.html',
    styleUrl: '../../app.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AsyncPipe, NgbPagination, NgbNavModule, SortableDirective, FormsModule]
})
export class MasterDataLoadsComponent implements OnDestroy {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChildren('fileInput') private fileInputs!: QueryList<ElementRef<HTMLInputElement>>;
    @ViewChild('detailModal') private detailModal!: TemplateRef<unknown>;
    @ViewChild('helpModal') private helpModal!: TemplateRef<unknown>;
    private detailModalRef!: NgbModalRef;
    private pollSubscription?: Subscription;
    protected readonly formatDate = formatDate;

    readonly fileKinds: FileKindOption[] = [
        {key: 'trading_partners', label: 'Trading Partners', abbr: 'TP'},
        {key: 'distribution_centers', label: 'Distribution Centers', abbr: 'DC'},
        {key: 'products', label: 'Products', abbr: 'Prod'},
        {key: 'price_history', label: 'Price History', abbr: 'Price'},
        {key: 'uom', label: 'UOM', abbr: 'UOM'},
    ];

    // Per-kind help text + a small sample (header + a couple of rows). Columns mirror the
    // engine's file_specs.py exactly (names + order); all files are tab-delimited.
    readonly fileGuides: Record<MasterDataFileKind, FileKindGuide> = {
        trading_partners: {
            description: 'Trading partners — required before Distribution Centers, which reference a TP_ID.',
            columns: ['TP_ID', 'NAME', 'ADDRESS1', 'ADDRESS2', 'CITY', 'STATE', 'ZIP_CODE', 'ABBREVIATION'],
            rows: [
                ['ABC', 'ABC', 'PO BOX 247', '', 'THOROFARE', 'NJ', '08086-0247', 'ABC'],
                ['AND', 'ANDA', '2915 WESTON ROAD', '', 'WESTON', 'FL', '33331', 'AND'],
            ],
        },
        distribution_centers: {
            description: 'DCs for a trading partner. Each TP_ID must resolve to a Trading Partner in the same bundle.',
            columns: [
                'TP_ID', 'NAME', 'ADDRESS1', 'ADDRESS2', 'CITY', 'STATE', 'ZIP_CODE', 'GLN', 'DEA', 'HIN',
                'DUNS', 'DUNS4', 'BUYER_ID', 'SELLER_ID', 'SAP_ID', 'NCPDP', 'IS_ACTIVE',
            ],
            rows: [
                ['ABC', 'ABC - BROOKS, KY', '345 INTERNATIONAL BLVD.', 'SUITE 400', 'Brooks', 'KY', '40109',
                    '', 'RA0219798', '', '', '0069066140200', '', '', 'ZP30000503', '', '1'],
                ['ABC', 'ABC - BUFORD, GA', '3930 MASON MILL ROAD N.E.', '', 'Buford', 'GA', '30518',
                    '', 'RA0515063', '', '', '0069066140039', '', '', 'ZP30000352', '', '1'],
            ],
        },
        products: {
            description: 'Product master keyed by NDC. NDC_2..NDC_11 alternate-identifier columns must be present as headers even when blank.',
            columns: [
                'NDC', 'NDC_QUALIFIER', 'NAME', 'DESCRIPTION', 'SIZE', 'STRENGTH', 'UOM', 'IS_ACTIVE', 'BM',
                'SCHEDULE', 'GENERIC_NAME',
                'NDC_2', 'NDC_QUALIFIER_2', 'NDC_3', 'NDC_QUALIFIER_3', 'NDC_4', 'NDC_QUALIFIER_4',
                'NDC_5', 'NDC_QUALIFIER_5', 'NDC_6', 'NDC_QUALIFIER_6', 'NDC_7', 'NDC_QUALIFIER_7',
                'NDC_8', 'NDC_QUALIFIER_8', 'NDC_9', 'NDC_QUALIFIER_9', 'NDC_10', 'NDC_QUALIFIER_10',
                'NDC_11', 'NDC_QUALIFIER_11',
            ],
            rows: [
                ['904549546', 'N4', 'Nitroglycerin Patch-Major', 'Nitroglycerin Patch-Major 0.2Mg (30 Ct)',
                    '30', '0.2mg', 'EA', '1', '24', '', 'Nitroglycerin', '904549546', 'ND'],
                ['904549646', 'N4', 'Nitroglycerin Patch-Major', 'Nitroglycerin Patch-Major 0.4Mg (30 Ct)',
                    '30', '0.4mg', 'EA', '1', '24', '', 'Nitroglycerin', '904549646', 'ND'],
            ],
        },
        price_history: {
            description: 'Effective-dated price changes per product (NDC + qualifier). Natural key is product + effective date.',
            columns: ['NDC', 'NDC_QUALIFIER', 'PRICE', 'EFFECTIVE_DATE'],
            rows: [
                ['00105551001', 'ND', '$1.00', '7/29/2021'],
                ['904549546', 'N4', '$0.00', '1/1/2019'],
            ],
        },
        uom: {
            description: 'Unit-of-measure conversions per product (e.g. case to each). Natural key is product + UOM code.',
            columns: ['NDC', 'NDC_QUALIFIER', 'UOM', 'MULTIPLIER'],
            rows: [
                ['12345678901', 'ND', 'CS', '24'],
                ['12345678901', 'ND', 'EA', '1'],
            ],
        },
    };

    customers: Customer[] = [];
    customerId: string | undefined;
    selectedFiles: Partial<Record<MasterDataFileKind, File>> = {};
    uploading = false;
    rejectionReport: ValidationReport | null = null;
    selectedDetail: MasterDataLoadRunDetail | undefined;
    detailViews: FileDetailView[] = [];
    activeTabId = 0;
    detailPageSize = 10;

    constructor(
        public globalService: GlobalService,
        private route: ActivatedRoute,
        private toastService: ToastService,
        private modalService: NgbModal,
        private apiService: ApiService,
        public service: MasterDataLoadsService,
    ) {
        this.customers = this.route.snapshot.data['resolvedData'].data;
    }

    ngOnDestroy() {
        this.pollSubscription?.unsubscribe();
    }

    onCustomerChange(event: Event) {
        this.customerId = (event.target as HTMLInputElement).value || undefined;
        this.resetUpload();
        if (this.customerId) {
            this.loadHistory();
        } else {
            this.service.data = [];
            this.service.refreshDataSource();
        }
    }

    onFileSelected(kind: MasterDataFileKind, event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            this.selectedFiles[kind] = file;
        } else {
            delete this.selectedFiles[kind];
        }
    }

    get hasSelectedFiles(): boolean {
        return Object.keys(this.selectedFiles).length > 0;
    }

    onReset() {
        this.resetUpload();
    }

    openHelp() {
        this.modalService.open(this.helpModal, {
            modalDialogClass: "modal-dialog-centered modal-xl modal-dialog-scrollable",
        });
    }

    // Build a tab-delimited sample (header + rows) and trigger a client-side download.
    // Rows are padded to the column count so trailing optional columns still align.
    downloadSample(kind: MasterDataFileKind) {
        const guide = this.fileGuides[kind];
        const lines = [guide.columns, ...guide.rows.map(row => this.padRow(row, guide.columns.length))];
        const content = lines.map(cells => cells.join('\t')).join('\n') + '\n';
        const blob = new Blob([content], {type: 'text/plain;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${kind}_sample.txt`;
        link.click();
        URL.revokeObjectURL(url);
    }

    private padRow(row: string[], length: number): string[] {
        return row.length >= length ? row : [...row, ...Array(length - row.length).fill('')];
    }

    onUpload() {
        if (!this.customerId) {
            this.toastService.showDanger("No customer", "Select a customer before uploading.");
            return;
        }

        const error = this.validateBundle();
        if (error) {
            this.toastService.showDanger("Invalid bundle", error);
            return;
        }

        const formData = new FormData();
        for (const {key} of this.fileKinds) {
            const file = this.selectedFiles[key];
            if (file) {
                formData.append(key, file, file.name);
            }
        }

        this.rejectionReport = null;
        this.uploading = true;
        this.globalService.showWaitOverlay("Validating bundle...");

        this.apiService.createMasterDataLoad(this.customerId, formData).subscribe({
            next: (response) => {
                this.globalService.hideWaitOverlay();
                this.uploading = false;

                if (response.status === 'REJECTED') {
                    this.rejectionReport = response.report;
                    this.toastService.showDanger("Validation failed", "The bundle was rejected. See the report below.");
                    return;
                }

                this.toastService.showSuccess("Queued", "The load was queued for processing.");
                this.resetUpload();
                this.loadHistory();
                this.startPolling();
            },
            error: (e) => {
                this.globalService.hideWaitOverlay();
                this.uploading = false;
                const message = e?.status === 409
                    ? "A load is already queued or processing for this customer."
                    : "Failed to submit the load. Please try again.";
                this.toastService.showDanger("Upload failed", message);
            }
        });
    }

    onViewDetail(run: MasterDataLoadRun) {
        this.globalService.showWaitOverlay("Loading run...");
        this.apiService.getMasterDataLoad(run.id).subscribe({
            next: (detail) => {
                this.globalService.hideWaitOverlay();
                this.selectedDetail = detail;
                this.detailViews = detail.files
                    .map(f => ({
                        file: f,
                        findings: this.parseFindings(f.messages),
                        page: 1,
                    }))
                    .sort((a, b) => this.fileKindOrder(a.file.fileKind) - this.fileKindOrder(b.file.fileKind));
                this.activeTabId = 0;
                this.detailModalRef = this.modalService.open(this.detailModal, {
                    modalDialogClass: "modal-dialog-centered modal-xl",
                    backdrop: "static",
                });
            },
            error: () => {
                this.globalService.hideWaitOverlay();
                this.toastService.showDanger("Request failed", "Failed to load run details.");
            }
        });
    }

    onSort({column, direction}: SortEvent) {
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = '';
            }
        }
        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    customerName(customerId: string): string {
        return this.customers.find(c => c.id === customerId)?.name ?? customerId;
    }

    parseFindings(messages: string | null): ValidationFinding[] {
        if (!messages) {
            return [];
        }
        try {
            return JSON.parse(messages) as ValidationFinding[];
        } catch {
            return [];
        }
    }

    pagedFindings(view: FileDetailView): ValidationFinding[] {
        const start = (view.page - 1) * this.detailPageSize;
        return view.findings.slice(start, start + this.detailPageSize);
    }

    // Page size is shared across tabs; reset every tab to page 1 so none is left out of range.
    resetDetailPages() {
        this.detailViews.forEach(v => v.page = 1);
    }

    fileKindLabel(fileKind: string): string {
        return this.fileKinds.find(k => k.key === fileKind)?.label ?? fileKind;
    }

    // Abbreviated chips for the history grid, ordered by the fileKinds natural flow.
    // Filtering fileKinds (rather than mapping the run's list) gives order + labels for free.
    runFileKindChips(fileKinds: string[]): FileKindOption[] {
        return this.fileKinds.filter(k => fileKinds.includes(k.key));
    }

    // Detail-modal tab order follows the fileKinds array (its natural upload flow);
    // unknown kinds sort last.
    private fileKindOrder(fileKind: string): number {
        const index = this.fileKinds.findIndex(k => k.key === fileKind);
        return index === -1 ? this.fileKinds.length : index;
    }

    statusBadgeClass(status: string): string {
        switch (status) {
            case 'SUCCEEDED': return 'bg-success bi-check';
            case 'LOADED': return 'bg-success bi-check';
            case 'FAILED': return 'bg-danger bi-exclamation-circle-fill';
            case 'REJECTED': return 'bg-warning text-dark bi-exclamation-triangle';
            case 'PROCESSING': return 'bg-info text-dark bi-arrow-clockwise';
            default: return 'bg-secondary'; // QUEUED + anything unknown
        }
    }

    private validateBundle(): string | null {
        const chosen = this.fileKinds.filter(k => this.selectedFiles[k.key]);
        if (chosen.length === 0) {
            return "Select at least one file.";
        }
        for (const {key, label} of chosen) {
            const file = this.selectedFiles[key]!;
            if (!file.name.toLowerCase().endsWith('.txt')) {
                return `${label} must be a .txt file.`;
            }
            if (file.size === 0) {
                return `${label} file is empty.`;
            }
        }
        // Cross-file: a Distribution Centers file needs a Trading Partners file in the same bundle.
        if (this.selectedFiles['distribution_centers'] && !this.selectedFiles['trading_partners']) {
            return "A Distribution Centers file requires a Trading Partners file in the same bundle.";
        }
        return null;
    }

    private loadHistory() {
        if (!this.customerId) {
            return;
        }
        this.apiService.getMasterDataLoads(this.customerId).subscribe({
            next: (runs) => {
                this.service.data = runs;
                this.service.refreshDataSource();
            },
            error: () => this.toastService.showDanger("Request failed", "Failed to load run history.")
        });
    }

    // After queuing, poll briefly so the grid reflects PROCESSING -> terminal without a manual refresh.
    private startPolling() {
        if (!this.customerId) {
            return;
        }
        const customerId = this.customerId;
        this.pollSubscription?.unsubscribe();
        this.pollSubscription = timer(3000, 3000).pipe(
            switchMap(() => this.apiService.getMasterDataLoads(customerId)),
            take(10),
        ).subscribe((runs) => {
            this.service.data = runs;
            this.service.refreshDataSource();
            const stillActive = runs.some(r => r.status === 'QUEUED' || r.status === 'PROCESSING');
            if (!stillActive) {
                this.pollSubscription?.unsubscribe();
            }
        });
    }

    private resetUpload() {
        this.selectedFiles = {};
        this.rejectionReport = null;
        // The model is cleared above, but native file inputs keep their chosen filename until reset.
        this.fileInputs?.forEach(input => input.nativeElement.value = '');
    }

    longFormatDate(input: string): string {
        return this.formatDate(input + "Z", "yyyy-MM-dd, hh:mm:ss a", "en-US")
    }
}
